import {
  type Communication,
  type DateTime,
  type Contact,
  type FilterGroup,
  type Language,
  type Lease,
  type Template,
  Colors,
  CommunicationChannel,
  CommunicationErrorCode,
  CommunicationStatus,
  CommunicationType,
  EMAIL_REGEX,
  extractEmailOrNumber,
  type Filter,
  FilterEntityType,
  getContactNameFromObject,
  getDefaultTemplates,
  getFilterEntityValuesForGroups,
  getSmsBodyLength,
  getTemplateTypeFromCommunicationChannel,
  calendarDateFromInstant,
  getLocalTimeZone,
  calendarDateFromTimestamp,
  formatCalendarDate,
  calendarDateTimeFromTimestamp,
  isAfterOrEqual,
  isBeforeOrEqual,
  isCommunicationInManagementFilter,
  isEntityInClassicFilters,
  isLetterChannel,
  isInstantAfter,
  isInstantBefore,
  isNilOrEmpty,
  isSendCommunicationEnable,
  mergeRecipients,
  normalizeString,
  todayDateTime,
  POSTBIRD_DELIVERED_STATUSES,
  POSTBIRD_ERROR_STATUSES,
  POSTBIRD_NOT_COLLECTED_STATUSES,
  SignatureDocumentStatus,
  TemplateType,
  TWILIO_MAX_LENGTH_FOR_BODY,
  isSentCommunicationStatus,
  MAX_SEND_RETRY_COUNT,
} from '@rentguru/commons-utils';
import { isValidPhoneNumber } from 'libphonenumber-js';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import type { IntlFormatters } from 'react-intl';
import * as Yup from 'yup';

import {
  TEMPLATE_CONTRACT_ROUTE,
  TEMPLATE_MAIL_ROUTE,
  TEMPLATE_SMS_ROUTE,
} from 'src/components/Templates/templateUtils';
import { verifyDisposableEmail } from 'src/components/ui/Forms/FormField/Contacts/contactFormUtils';
import type { CommunicationsFilters, FilterFromTo } from 'src/hooks/FiltersContext';
import { stripHTML } from 'src/utils/stripHTML';

export enum CommunicationFilterType {
  TO_SEND = 'TO_SEND',
  HISTORY = 'HISTORY',
  OUTBOX = 'OUTBOX',
  OUTDATED = 'OUTDATED',
}

export interface CommunicationWithTemplate extends Communication {
  template?: Template;
}

export const addTemplateToCommunication = (
  communication: Communication,
  templates: Template[]
): CommunicationWithTemplate => {
  const communicationTemplateId = communication.communicationTemplateId;
  if (isNil(communicationTemplateId)) {
    return communication;
  }

  const customTemplate = templates.find((template) => template.id === communicationTemplateId);
  if (!isNil(customTemplate)) {
    return { ...communication, template: customTemplate };
  }

  const templateType = getTemplateTypeFromCommunicationChannel(communication.channel);
  if (isNil(templateType)) {
    return communication;
  }

  const defaultTemplates = getDefaultTemplates(templateType);
  const defaultTemplate = defaultTemplates.find((template) => template.id === communicationTemplateId);
  if (!isNil(defaultTemplate)) {
    return { ...communication, template: defaultTemplate };
  }
  return communication;
};

const stripHtmlAndTemplateTagTokens = (text: string): string => stripHTML(text).replace(/\{\{[^{}]*\}\}/g, ' ');

export const buildCommunicationSearchBlob = (
  communication: Pick<Communication, 'subject' | 'recipient' | 'recipientInBcc' | 'recipientInCc'>,
  body: string,
  recipientContacts: Contact[],
  leaseName: string | undefined
): string => {
  const parts = [
    stripHtmlAndTemplateTagTokens(communication.subject ?? ''),
    stripHtmlAndTemplateTagTokens(body),
    communication.recipient ?? '',
    communication.recipientInBcc ?? '',
    communication.recipientInCc ?? '',
    ...recipientContacts.map((contact) => getContactNameFromObject(contact)),
    leaseName ?? '',
  ];
  return normalizeString(parts.join(' ')).toLowerCase();
};

export const communicationMatchesSearchTokens = (blob: string, searchTokens: string[]): boolean =>
  searchTokens.every((token) => blob.includes(token));

export const resolveContactsFromIds = (
  contactIds: Array<string> | null | undefined,
  getContact: (contactId: string) => Contact | undefined
): Contact[] => uniqBy(compact((contactIds ?? []).map((contactId) => getContact(contactId))), (contact) => contact.id);

interface ResolvedRecipientContacts {
  deliveryContacts: Contact[];
  concernedContacts: Contact[];
}

export const resolveRecipientContacts = (
  communication: Pick<Communication, 'contactIds' | 'concernedContactIds'>,
  getContact: (contactId: string) => Contact | undefined
): ResolvedRecipientContacts => {
  const deliveryContacts = resolveContactsFromIds(communication.contactIds, getContact);
  const deliveryIds = new Set(deliveryContacts.map((contact) => contact.id));
  const concernedContacts = resolveContactsFromIds(communication.concernedContactIds, getContact).filter(
    (contact) => !deliveryIds.has(contact.id)
  );
  return { deliveryContacts, concernedContacts };
};

export interface CommunicationGroup {
  key: string;
  label: string;
}

export const getCommunicationRecipientGroup = (
  communication: Communication,
  getContact: (contactId: string) => Contact | undefined
): CommunicationGroup => {
  const { deliveryContacts, concernedContacts } = resolveRecipientContacts(communication, getContact);
  if (isEmpty(deliveryContacts) && isEmpty(concernedContacts)) {
    const rawRecipients = mergeRecipients([
      communication.recipient,
      communication.recipientInBcc,
      communication.recipientInCc,
    ]);
    return { key: rawRecipients, label: processRecipients(rawRecipients) };
  }
  const concernedNames = concernedContacts.map(getContactNameFromObject).join(', ');
  const deliveryNames = deliveryContacts.map(getContactNameFromObject).join(', ');
  const contactIds = (contacts: Contact[]) =>
    contacts
      .map((contact) => contact.id)
      .sort()
      .join(',');
  const key = `concerned:${contactIds(concernedContacts)}/delivery:${contactIds(deliveryContacts)}`;
  if (isEmpty(concernedNames)) {
    return { key, label: deliveryNames };
  }
  if (isEmpty(deliveryNames)) {
    return { key, label: concernedNames };
  }
  return { key, label: `${concernedNames} \u2192 ${deliveryNames}` };
};

const deleteEmailFromRecipient = (recipient: string) => recipient.split('<')[0].trim();

export const processRecipients = (recipients: string): string => {
  if (isEmpty(recipients)) {
    return '';
  }
  const splittedRecipients = recipients.split(',').map((recipient) => deleteEmailFromRecipient(recipient));
  if (splittedRecipients.length > 2) {
    return `${splittedRecipients[0]}, ${splittedRecipients[1]},...`;
  }
  return splittedRecipients.join(', ');
};

export enum CommunicationDisplayStatus {
  DRAFT = 'DRAFT',
  TO_SEND = 'TO_SEND',
  SCHEDULED = 'SCHEDULED',
  SENDING = 'SENDING',
  SENT = 'SENT',
  SENT_MANUALLY = 'SENT_MANUALLY',
  DOWNLOADED = 'DOWNLOADED',
  IGNORED = 'IGNORED',
  ERROR = 'ERROR',
  OUTDATED = 'OUTDATED',
  LETTER_IN_TRANSIT = 'LETTER_IN_TRANSIT',
  LETTER_DELIVERED = 'LETTER_DELIVERED',
  LETTER_NOT_COLLECTED = 'LETTER_NOT_COLLECTED',
}

export const COMMUNICATION_DISPLAY_STATUS_COLORS: Record<CommunicationDisplayStatus, Colors> = {
  [CommunicationDisplayStatus.DRAFT]: Colors.CARROT_ORANGE,
  [CommunicationDisplayStatus.TO_SEND]: Colors.DODGER_BLUE,
  [CommunicationDisplayStatus.SCHEDULED]: Colors.BLUEY,
  [CommunicationDisplayStatus.SENDING]: Colors.TURQUOISE_BLUE,
  [CommunicationDisplayStatus.SENT]: Colors.MALACHITE_GREEN,
  [CommunicationDisplayStatus.SENT_MANUALLY]: Colors.MINTY_GREEN,
  [CommunicationDisplayStatus.DOWNLOADED]: Colors.SLATE_GREY,
  [CommunicationDisplayStatus.IGNORED]: Colors.TOWER_GREY,
  [CommunicationDisplayStatus.ERROR]: Colors.CARNATION_RED,
  [CommunicationDisplayStatus.OUTDATED]: Colors.TOWER_GREY,
  [CommunicationDisplayStatus.LETTER_IN_TRANSIT]: Colors.DODGER_BLUE,
  [CommunicationDisplayStatus.LETTER_DELIVERED]: Colors.HAZE_GREEN,
  [CommunicationDisplayStatus.LETTER_NOT_COLLECTED]: Colors.CARROT_ORANGE,
};

const getLetterDisplayStatus = (externalStatus: string): CommunicationDisplayStatus => {
  if (POSTBIRD_ERROR_STATUSES.includes(externalStatus)) {
    return CommunicationDisplayStatus.ERROR;
  }
  if (POSTBIRD_NOT_COLLECTED_STATUSES.includes(externalStatus)) {
    return CommunicationDisplayStatus.LETTER_NOT_COLLECTED;
  }
  if (POSTBIRD_DELIVERED_STATUSES.includes(externalStatus)) {
    return CommunicationDisplayStatus.LETTER_DELIVERED;
  }
  return CommunicationDisplayStatus.LETTER_IN_TRANSIT;
};

export const getCommunicationDisplayStatus = (
  communication: Pick<Communication, 'channel' | 'status' | 'toSend' | 'externalStatus' | 'sentAt'>
): CommunicationDisplayStatus => {
  const isLetter = isLetterChannel(communication.channel);
  if (isLetter && !isNilOrEmpty(communication.externalStatus)) {
    return getLetterDisplayStatus(communication.externalStatus);
  }
  switch (communication.status) {
    case CommunicationStatus.DRAFT:
      return CommunicationDisplayStatus.DRAFT;
    case CommunicationStatus.SENDING:
      return CommunicationDisplayStatus.SENDING;
    case CommunicationStatus.SENT:
      return CommunicationDisplayStatus.SENT;
    case CommunicationStatus.DOWNLOADED:
      return CommunicationDisplayStatus.DOWNLOADED;
    case CommunicationStatus.SENT_MANUALLY:
      return CommunicationDisplayStatus.SENT_MANUALLY;
    case CommunicationStatus.IGNORED:
      return CommunicationDisplayStatus.IGNORED;
    case CommunicationStatus.ERROR:
      return CommunicationDisplayStatus.ERROR;
    case CommunicationStatus.OUTDATED:
      return CommunicationDisplayStatus.OUTDATED;
    case CommunicationStatus.SCHEDULED:
      return CommunicationDisplayStatus.SCHEDULED;
    default:
      if (communication.toSend || (isLetter && !isNilOrEmpty(communication.sentAt))) {
        return CommunicationDisplayStatus.SENDING;
      }
      return CommunicationDisplayStatus.TO_SEND;
  }
};

export const isCommunicationBeingSent = (communication: Communication): boolean =>
  getCommunicationDisplayStatus(communication) === CommunicationDisplayStatus.SENDING;

export { MAX_SEND_RETRY_COUNT } from '@rentguru/commons-utils';

export enum CommunicationDeliveryState {
  RETRY_PENDING = 'RETRY_PENDING',
  DELIVERED = 'DELIVERED',
  RETRY_EXHAUSTED = 'RETRY_EXHAUSTED',
  BOUNCED = 'BOUNCED',
  NONE = 'NONE',
}

export const getPendingRetryDateTime = (
  communication: Pick<Communication, 'nextRetryAt'> | null | undefined
): DateTime | null => {
  const nextRetryAt = communication?.nextRetryAt;
  if (isNilOrEmpty(nextRetryAt)) {
    return null;
  }
  const nextRetry = calendarDateTimeFromTimestamp(nextRetryAt);
  if (!isInstantAfter(nextRetry, todayDateTime())) {
    return null;
  }
  return nextRetry;
};

export const getCommunicationDeliveryState = (
  communication: Pick<Communication, 'deliveredAt' | 'nextRetryAt' | 'sendRetryCount'> | null | undefined,
  hasBounced: boolean
): CommunicationDeliveryState => {
  if (!isNil(getPendingRetryDateTime(communication))) {
    return CommunicationDeliveryState.RETRY_PENDING;
  }
  if (!isNilOrEmpty(communication?.deliveredAt)) {
    return CommunicationDeliveryState.DELIVERED;
  }
  if (!hasBounced) {
    return CommunicationDeliveryState.NONE;
  }
  if ((communication?.sendRetryCount ?? 0) >= MAX_SEND_RETRY_COUNT) {
    return CommunicationDeliveryState.RETRY_EXHAUSTED;
  }
  return CommunicationDeliveryState.BOUNCED;
};

type CommunicationInError = Pick<
  Communication,
  'channel' | 'status' | 'errorCode' | 'externalStatus' | 'nextRetryAt' | 'signatureDocument'
>;

export const hasCommunicationFailed = (communication: Pick<Communication, 'status'>): boolean =>
  communication.status === CommunicationStatus.ERROR;

export const isUnsentCommunicationStatus = (status?: CommunicationStatus | null): boolean =>
  !isNil(status) && [CommunicationStatus.ERROR, CommunicationStatus.TO_SEND].includes(status);

export const hasCommunicationBounced = (communication: Pick<Communication, 'status' | 'errorCode'>): boolean =>
  hasCommunicationFailed(communication) && communication.errorCode === CommunicationErrorCode.BOUNCED;

const hasSignatureDocumentFailed = (communication: Pick<Communication, 'signatureDocument'>): boolean => {
  const signatureDocumentStatus = communication.signatureDocument?.status;
  if (isNil(signatureDocumentStatus)) {
    return false;
  }
  return [SignatureDocumentStatus.ERROR, SignatureDocumentStatus.REJECTED].includes(signatureDocumentStatus);
};

const hasLetterFailed = (communication: CommunicationInError): boolean => {
  if (!isLetterChannel(communication.channel)) {
    return false;
  }
  if (!isNilOrEmpty(communication.externalStatus) && POSTBIRD_ERROR_STATUSES.includes(communication.externalStatus)) {
    return true;
  }
  return hasSignatureDocumentFailed(communication);
};

export const getCommunicationErrorMessage = (
  communication: CommunicationInError,
  formatMessage: IntlFormatters['formatMessage'],
  language: Language
): string => {
  const pendingRetryDateTime = getPendingRetryDateTime(communication);
  if (!isNil(pendingRetryDateTime)) {
    return formatMessage(
      { id: 'communications.list.temporaryBounceTooltip' },
      { date: formatCalendarDate(calendarDateFromInstant(pendingRetryDateTime, getLocalTimeZone()), language) }
    );
  }
  const { errorCode } = communication;
  if (errorCode === CommunicationErrorCode.SENDER_NOT_CONFIGURED && isLetterChannel(communication.channel)) {
    return formatMessage({ id: 'communications.detail.senderAddressNotConfigured' });
  }
  if (!isNil(errorCode)) {
    return formatMessage({ id: `enums.CommunicationErrorCodes.${errorCode}` });
  }
  if (hasLetterFailed(communication)) {
    return formatMessage({
      id: `communications.detail.${
        communication.channel === CommunicationChannel.REGISTERED_LETTER ? 'registeredLetterError' : 'letterError'
      }`,
    });
  }
  if (communication.status === CommunicationStatus.ERROR) {
    return formatMessage({ id: `communications.status.${CommunicationDisplayStatus.ERROR}` });
  }
  return '';
};

export const filterCommunicationsByFilters = (
  communications: Communication[],
  communicationFilters: CommunicationsFilters,
  filterType: CommunicationFilterType,
  allCompleteLeases: Lease[],
  managementFilters?: FilterGroup[]
) => {
  const { filters, communicationsFromTo } = communicationFilters;
  const { typeFilter, classicFilters, statusFilter, recipientFilter } = extractFilters(filters, filterType);
  const hasManagerFilters = !isNilOrEmpty(managementFilters);
  const unitIdsToKeep = hasManagerFilters
    ? getFilterEntityValuesForGroups(managementFilters, FilterEntityType.UNIT, 'id')
    : [];

  return communications.filter((communication) => {
    if (!isEmpty(classicFilters) && !isEntityInClassicFilters(communication, classicFilters)) {
      return false;
    }
    if (
      !isNil(typeFilter) &&
      !isEmpty(typeFilter.items) &&
      !communicationHasCorrectType(communication, typeFilter.items as CommunicationType[])
    ) {
      return false;
    }

    if (
      !isNil(statusFilter) &&
      !isEmpty(statusFilter.items) &&
      !communicationHasCorrectStatus(communication, statusFilter.items as CommunicationDisplayStatus[])
    ) {
      return false;
    }

    if (
      !isNil(recipientFilter) &&
      !isEmpty(recipientFilter.items) &&
      !communicationHasCorrectRecipient(communication, recipientFilter.items)
    ) {
      return false;
    }

    if (!isNil(communicationsFromTo) && !communicationIsInInterval(communication, communicationsFromTo, filterType)) {
      return false;
    }

    if (hasManagerFilters) {
      return isCommunicationInManagementFilter(managementFilters, communication, allCompleteLeases, unitIdsToKeep);
    }
    return true;
  });
};

interface ExtractedFilters {
  typeFilter?: Filter;
  statusFilter?: Filter;
  recipientFilter?: Filter;
  classicFilters: Filter[];
}

const extractFilters = (filters: Filter[], filterType: CommunicationFilterType) =>
  filters.reduce<ExtractedFilters>(
    (accumulator, filter) => {
      switch (filter.name) {
        case 'type':
          accumulator.typeFilter = filter;
          break;
        case 'status':
          accumulator.statusFilter = filter;
          break;
        case 'recipient':
          if (filterType === CommunicationFilterType.OUTBOX) {
            break;
          }
          accumulator.recipientFilter = filter;
          break;
        default:
          accumulator.classicFilters.push(filter);
      }
      return accumulator;
    },
    {
      typeFilter: undefined,
      statusFilter: undefined,
      recipientFilter: undefined,
      classicFilters: [],
    }
  );

const communicationHasCorrectStatus = (communication: Communication, statuses: CommunicationDisplayStatus[]) =>
  statuses.includes(getCommunicationDisplayStatus(communication));

const communicationHasCorrectType = (communication: Communication, types: CommunicationType[]) =>
  types.some((type) => communication.type === type);

const communicationHasCorrectRecipient = (communication: Communication, recipientIdentifiers: string[]) => {
  if ((communication.contactIds ?? []).some((contactId) => recipientIdentifiers.includes(contactId))) {
    return true;
  }
  return !isNil(communication.recipient) && recipientIdentifiers.includes(communication.recipient);
};

export { isSentCommunicationStatus, SENT_COMMUNICATION_STATUSES } from '@rentguru/commons-utils';

export const isCommunicationQuickSendable = (
  communication: Communication,
  sendingSourcesWellConfigured: boolean
): boolean =>
  communication.status !== CommunicationStatus.DRAFT &&
  !hasCommunicationFailed(communication) &&
  isSendCommunicationEnable(communication, sendingSourcesWellConfigured);

export const isCommunicationIgnorable = (communication: Pick<Communication, 'type' | 'status'>): boolean =>
  communication.type !== CommunicationType.CUSTOM &&
  communication.status !== CommunicationStatus.DRAFT &&
  communication.status !== CommunicationStatus.IGNORED &&
  !isSentCommunicationStatus(communication.status);

const isCommunicationBlocked = (communication: Communication): boolean => {
  if (communication.status === CommunicationStatus.ERROR) {
    return true;
  }
  return isLetterChannel(communication.channel) && hasSignatureDocumentFailed(communication);
};

export const compareCommunicationsByBlockedThenCreatedAtDesc = (
  communicationA: Communication,
  communicationB: Communication
): number => {
  const isCommunicationABlocked = isCommunicationBlocked(communicationA);
  const isCommunicationBBlocked = isCommunicationBlocked(communicationB);
  if (isCommunicationABlocked !== isCommunicationBBlocked) {
    return isCommunicationABlocked ? -1 : 1;
  }
  const createdAtA = communicationA.createdAt ?? '';
  const createdAtB = communicationB.createdAt ?? '';
  if (createdAtB < createdAtA) {
    return -1;
  }
  if (createdAtB > createdAtA) {
    return 1;
  }
  return 0;
};

const communicationIsInInterval = (
  communication: Communication,
  communicationsFromTo: FilterFromTo,
  filterType: CommunicationFilterType
) => {
  const { from, to } = communicationsFromTo;
  const communicationDateString = [CommunicationFilterType.TO_SEND, CommunicationFilterType.OUTBOX].includes(filterType)
    ? communication.createdAt
    : communication.sentAt;
  if (isNil(communicationDateString)) {
    return false;
  }
  const communicationDate = calendarDateFromTimestamp(communicationDateString, getLocalTimeZone());
  if (!isNil(from) && !isAfterOrEqual(communicationDate, from)) {
    return false;
  }
  if (!isNil(to) && !isBeforeOrEqual(communicationDate, to)) {
    return false;
  }
  return true;
};

export const isScheduledInstantPast = (scheduledAt: DateTime): boolean => isInstantBefore(scheduledAt, todayDateTime());

export const isCommunicationManuallySendable = (communication: Pick<Communication, 'status'>): boolean =>
  communication.status !== CommunicationStatus.DRAFT && !isSentCommunicationStatus(communication.status);

export const isCommunicationEditable = (communication: Communication): boolean => {
  if (isSentCommunicationStatus(communication.status) || isCommunicationBeingSent(communication)) {
    return false;
  }
  if (communication.toSend === true) {
    return false;
  }
  if (!isLetterChannel(communication.channel)) {
    return true;
  }
  return isNilOrEmpty(communication.externalStatus) || hasCommunicationFailed(communication);
};

export const channelToTemplateType = (channel: CommunicationChannel) => {
  switch (channel) {
    case CommunicationChannel.SMS:
      return TemplateType.SMS;
    default:
      return TemplateType.MAIL;
  }
};

export const channelToTemplateRoute = (channel: CommunicationChannel) => {
  switch (channel) {
    case CommunicationChannel.REGISTERED_LETTER:
    case CommunicationChannel.LETTER:
    case CommunicationChannel.EMAIL:
      return TEMPLATE_MAIL_ROUTE;
    case CommunicationChannel.SMS:
      return TEMPLATE_SMS_ROUTE;
    default:
      return TEMPLATE_CONTRACT_ROUTE;
  }
};

export const getValidEmailSchema = (formatMessage: IntlFormatters['formatMessage']) =>
  Yup.array().of(
    Yup.string()
      // eslint-disable-next-line func-names
      .test('Checking email validity', 'disposable email error', async function (value) {
        if (isNil(value)) {
          return false;
        }
        const email = extractEmailOrNumber(value);
        const isEmailValid = EMAIL_REGEX.test(email);
        if (!isEmailValid) {
          return false;
        }
        // eslint-disable-next-line react/no-this-in-sfc
        const isDisposable = await verifyDisposableEmail(email, formatMessage, this.createError);
        return isDisposable === true;
      })
  );

export const getValidPhoneNumbersSchema = () =>
  Yup.array().of(
    Yup.string().test('Checking number validity', 'Phone number error', (value) => {
      if (isNil(value)) {
        return false;
      }
      const phoneNumber = extractEmailOrNumber(value);
      return isValidPhoneNumber(phoneNumber);
    })
  );

export const getValidSmsBodySchema = () =>
  Yup.string().test(
    'Checking body size',
    'Sms body too large',
    (value) => getSmsBodyLength(value ?? '') < TWILIO_MAX_LENGTH_FOR_BODY
  );
